/**
 * The "new studio" dialog: a name, an optional id (the one groups' grants
 * name, D14) and site, and what to start from — a template (`acp
 * studio-template`'s: the three simulated devices on a host and port block,
 * or one vMix on its host and TCP API port, docs/vmix.md) or an empty config
 * to fill in on the studio page. The config itself is built by the container
 * from the one template source (`@acp/engine/templates.js`).
 */
import { entityIdWireSchema } from '@acp/api-contract/tenancy.js';
import { DEFAULT_VMIX_PORT, type TemplateKind, type TemplateTarget } from '@acp/engine/templates.js';
import { templateTargetOf } from '../../lib/studio/templateTarget.js';
import { SimFields, VmixFields } from './StudioTemplateFields.js';
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

/** An optional id field's error against the contract's id alphabet (`entityIdWireSchema`, a key segment), or null. */
function idError(text: string): string | null {
  return text.trim() === '' || entityIdWireSchema.safeParse(text.trim()).success ? null : 'Letters, digits, - and _ only, up to 64.';
}

export type StudioRequest = {
  readonly name: string;
  readonly studioId: string | null;
  readonly siteId: string | null;
  /** Null: an empty config. */
  readonly template: TemplateTarget | null;
  /** Its default show style (D32): an existing one, a new one made from the template's show style (of the template's kind), or none. */
  readonly defaultShowStyle: { readonly kind: 'existing'; readonly showStyleId: string } | { readonly kind: 'template' } | null;
};

export type CreateStudioDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The simulated devices' defaults: this page's host, the primary checkout's port block. */
  readonly defaultHost: string;
  readonly defaultBasePort: number;
  /** The tenant's show styles, one of which can be the studio's default. */
  readonly showStyles: readonly { readonly id: string; readonly name: string }[];
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (request: StudioRequest) => void;
  /** What "Start from" shows first; the simulated devices unless given. */
  readonly initialStart?: TemplateKind | 'none';
};

/** The default-show-style select's "new from the template" value: a space cannot be in an id. */
const TEMPLATE_CHOICE = ' template';

type Start = TemplateKind | 'none';

/** The template the form describes, or null (an empty config, or a field not filled in). */
function startTarget(start: Start, sim: { readonly host: string; readonly port: string }, vmix: { readonly host: string; readonly port: string }): TemplateTarget | null {
  if (start === 'none') return null;
  const { host, port } = start === 'vmix' ? vmix : sim;
  return templateTargetOf(start, host, port);
}

function CreateStudioForm({ onOpenChange, defaultHost, defaultBasePort, showStyles, pending, error, onSubmit, initialStart }: Omit<CreateStudioDialogProps, 'open'>) {
  const [name, setName] = useState('');
  const [studioId, setStudioId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [start, setStart] = useState<Start>(initialStart ?? 'sim');
  const [host, setHost] = useState(defaultHost);
  const [basePort, setBasePort] = useState(String(defaultBasePort));
  const [vmixHost, setVmixHost] = useState('');
  const [vmixPort, setVmixPort] = useState(String(DEFAULT_VMIX_PORT));
  const fromTemplate = start !== 'none';
  // '': none; TEMPLATE_CHOICE: a new show style from the template; else an existing id. Shown: the pick while it is still a choice.
  const [pickedShowStyle, setShowStyle] = useState<string | null>(null);
  // Unpicked, a vMix studio gets the vMix show style (an existing one is mapped for other devices, most likely).
  const unpicked = start === 'vmix' ? TEMPLATE_CHOICE : (showStyles[0]?.id ?? (fromTemplate ? TEMPLATE_CHOICE : ''));
  const shownShowStyle = pickedShowStyle !== null && (pickedShowStyle === '' || pickedShowStyle === TEMPLATE_CHOICE || showStyles.some((s) => s.id === pickedShowStyle)) ? pickedShowStyle : unpicked;
  const studioIdError = idError(studioId);
  const siteIdError = idError(siteId);
  const target = startTarget(start, { host, port: basePort }, { host: vmixHost, port: vmixPort });
  const ready = name.trim() !== '' && studioIdError === null && siteIdError === null && (!fromTemplate || target !== null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    onSubmit({
      name: name.trim(),
      studioId: studioId.trim() === '' ? null : studioId.trim(),
      siteId: siteId.trim() === '' ? null : siteId.trim(),
      template: target,
      defaultShowStyle: shownShowStyle === '' ? null : shownShowStyle === TEMPLATE_CHOICE ? { kind: 'template' } : { kind: 'existing', showStyleId: shownShowStyle },
    });
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>New studio</DialogTitle>
        <DialogDescription>Start from a template — the three simulated devices, or one vMix — mapped to its show style’s layers, or from nothing.</DialogDescription>
      </DialogHeader>
      <Field>
        <Label htmlFor="studio-name">Name</Label>
        <Input id="studio-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} autoFocus />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field invalid={studioIdError !== null}>
          <Label htmlFor="studio-id">Id (optional)</Label>
          <Input id="studio-id" value={studioId} onChange={(e) => setStudioId(e.target.value)} disabled={pending} aria-invalid={studioIdError !== null} placeholder="studio-a" />
          <FieldError>{studioIdError}</FieldError>
        </Field>
        <Field invalid={siteIdError !== null}>
          <Label htmlFor="studio-site">Site (optional)</Label>
          <Input id="studio-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={pending} aria-invalid={siteIdError !== null} />
          <FieldError>{siteIdError}</FieldError>
        </Field>
      </div>
      <Field>
        <Label htmlFor="studio-start">Start from</Label>
        <NativeSelect id="studio-start" value={start} onChange={(e) => setStart(e.target.value === 'vmix' ? 'vmix' : e.target.value === 'none' ? 'none' : 'sim')} disabled={pending} data-testid="studio-start">
          <option value="sim">The simulated devices</option>
          <option value="vmix">vMix studio</option>
          <option value="none">Nothing (an empty config)</option>
        </NativeSelect>
      </Field>
      {start === 'sim' ? <SimFields host={host} basePort={basePort} pending={pending} onHost={setHost} onBasePort={setBasePort} /> : null}
      {start === 'vmix' ? <VmixFields host={vmixHost} port={vmixPort} pending={pending} onHost={setVmixHost} onPort={setVmixPort} /> : null}
      <Field>
        <Label htmlFor="studio-default-show-style">Default show style</Label>
        <NativeSelect id="studio-default-show-style" value={shownShowStyle} onChange={(e) => setShowStyle(e.target.value)} disabled={pending}>
          <option value="">(none)</option>
          <option value={TEMPLATE_CHOICE}>{start === 'vmix' ? 'New from the vMix show-style template' : 'New from the show-style template'}</option>
          {showStyles.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </NativeSelect>
        <FieldDescription>What its productions run unless they name another show style (D32). Its devices are mapped to that show style's layers.</FieldDescription>
      </Field>
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ready || pending} data-testid="create-studio-submit">
          {pending ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function CreateStudioDialog({ open, ...rest }: CreateStudioDialogProps) {
  return (
    <Dialog open={open} onOpenChange={rest.onOpenChange}>
      <DialogContent data-testid="create-studio-dialog">
        <CreateStudioForm {...rest} />
      </DialogContent>
    </Dialog>
  );
}
